import { createReducer, on } from '@ngrx/store';

import { TemplatePlaceholder } from '@main-application/shared/template-editor-dialog/services/template-editor-dialog.service';
import { RestUnitRenewalTargetModel } from '@shared/interfaces/renevals.interface';
import { RestThresholdSection } from '@shared/interfaces/rest-threshold-section';

import {
  changeCustomFieldAccess,
  changeCustomFieldAccessFailure,
  changeCustomFieldAccessSuccess,
  createBaseCustomFieldByContextId,
  createBaseCustomFieldByContextIdFailure,
  createBaseCustomFieldByContextIdSuccess,
  createCustomField,
  createCustomFieldFailure,
  createCustomFieldSuccess,
  deleteBaseCustomFieldByContextId,
  deleteBaseCustomFieldByContextIdFailure,
  deleteBaseCustomFieldByContextIdSuccess,
  deleteCustomField,
  deleteCustomFieldFailure,
  deleteCustomFieldSuccess,
  getRenewalTargetList,
  getRenewalTargetListFailure,
  getRenewalTargetListSuccess,
  loadAllThresholds,
  loadAllThresholdsFailed,
  loadAllThresholdsSuccess,
  loadCustomFieldByContextId,
  loadCustomFieldByContextIdFailure,
  loadCustomFieldByContextIdSuccess,
  loadCustomFields,
  loadCustomFieldsFailure,
  loadCustomFieldsSuccess,
  loadTemplatePlaceholders,
  loadTemplatePlaceholdersFailure,
  loadTemplatePlaceholdersSuccess,
  loadThresholdsProperty,
  loadThresholdsPropertyFailed,
  loadThresholdsPropertySuccess,
  loadThresholdsTask,
  loadThresholdsTaskFailed,
  loadThresholdsTaskSuccess,
  setThresholdsConfig,
  setThresholdsConfigFailed,
  setThresholdsConfigSuccess,
  updateBaseCustomFieldByContextId,
  updateBaseCustomFieldByContextIdFailure,
  updateBaseCustomFieldByContextIdSuccess,
  updateCustomField,
  updateCustomFieldFailure,
  updateCustomFieldSuccess,
  updateRenewalTarget,
  updateRenewalTargetFailure,
  updateRenewalTargetSuccess,
} from './management.actions';
import {
  CustomFieldModel,
  PropertyBaseCustomFieldsDataModel,
} from '../pages/property-model/services/custom-fields.service';

export const managementFeatureKey = 'management';

export interface ManagementState {
  thresholdSectionLoading: boolean;
  thresholdSections: RestThresholdSection[];
  thresholdSectionPropertyLoading: boolean;
  thresholdSectionProperty: RestThresholdSection[];
  thresholdSectionTaskLoading: boolean;
  thresholdSectionTask: RestThresholdSection[];
  thresholdUpdateLoading: boolean;
  renewalTargetList: RestUnitRenewalTargetModel[];
  renewalTargetListLoading: boolean;
  updateRenewalTargetModelLoading: boolean;
  customFields: CustomFieldModel[];
  createCustomFieldLoading: boolean;
  deleteCustomFieldLoading: boolean;
  customFieldsLoading: boolean;
  updateCustomFieldLoading: boolean;
  CustomFieldByContextId?: Record<number, PropertyBaseCustomFieldsDataModel[]>;
  CustomFieldByContextIdLoading?: boolean;
  changeCustomFieldAccessLoading?: boolean;
  templatePlaceholders?: TemplatePlaceholder[];
  templatePlaceholdersLoaded?: boolean;
  templatePlaceholdersLoading?: boolean;
}

export const initialState: ManagementState = {
  thresholdSectionLoading: false,
  thresholdSections: [],
  thresholdSectionPropertyLoading: false,
  thresholdSectionProperty: [],
  thresholdSectionTaskLoading: false,
  thresholdSectionTask: [],
  thresholdUpdateLoading: false,
  renewalTargetList: [],
  renewalTargetListLoading: false,
  updateRenewalTargetModelLoading: false,
  customFields: [],
  createCustomFieldLoading: false,
  deleteCustomFieldLoading: false,
  customFieldsLoading: false,
  updateCustomFieldLoading: false,
  CustomFieldByContextId: {},
  CustomFieldByContextIdLoading: false,
  changeCustomFieldAccessLoading: false,
  templatePlaceholders: [],
  templatePlaceholdersLoaded: false,
  templatePlaceholdersLoading: false,
};

export const reducer = createReducer(
  initialState,
  /**
   * Threshold sections
   */
  on(loadAllThresholds, (state, action) => ({ ...state, thresholdSectionLoading: true })),
  on(loadAllThresholdsSuccess, (state, action) => ({
    ...state,
    thresholdSectionLoading: false,
    thresholdSections: action.thresholdSections,
  })),
  on(loadAllThresholdsFailed, (state, action) => ({ ...state, thresholdSectionLoading: false, thresholdSections: [] })),

  /**
   * Thresholds Property
   */
  on(loadThresholdsProperty, (state, action) => ({ ...state, thresholdSectionPropertyLoading: true })),
  on(loadThresholdsPropertySuccess, (state, action) => ({
    ...state,
    thresholdSectionPropertyLoading: false,
    thresholdSectionProperty: action.thresholdProperty,
  })),
  on(loadThresholdsPropertyFailed, (state, action) => ({
    ...state,
    thresholdSectionPropertyLoading: false,
    thresholdSectionProperty: null,
  })),

  /**
   * Thresholds task
   */
  on(loadThresholdsTask, (state, action) => ({ ...state, thresholdSectionTaskLoading: true })),
  on(loadThresholdsTaskSuccess, (state, action) => ({
    ...state,
    thresholdSectionTaskLoading: false,
    thresholdSectionTask: action.thresholdTask,
  })),
  on(loadThresholdsTaskFailed, (state, action) => ({
    ...state,
    thresholdSectionTaskLoading: false,
    thresholdSectionTask: [],
  })),

  /**
   * Threshold update
   */
  on(setThresholdsConfig, (state, action) => ({ ...state, thresholdUpdateLoading: true })),
  on(setThresholdsConfigSuccess, (state, action) => ({
    ...state,
    thresholdUpdateLoading: false,
  })),
  on(setThresholdsConfigFailed, (state, action) => ({
    ...state,
    thresholdUpdateLoading: false,
  })),

  /**
   * get renewal target list
   */
  on(getRenewalTargetList, (state, action) => ({ ...state, renewalTargetListLoading: true })),
  on(getRenewalTargetListSuccess, (state, action) => ({
    ...state,
    renewalTargetList: action.renewalTargetList,
    renewalTargetListLoading: false,
  })),
  on(getRenewalTargetListFailure, (state, action) => ({
    ...state,
    renewalTargetList: [],
    renewalTargetListLoading: false,
  })),

  /**
   * update renewal target
   */
  on(updateRenewalTarget, (state, action) => ({ ...state, updateRenewalTargetModelLoading: true })),
  on(updateRenewalTargetSuccess, (state, action) => ({
    ...state,
    updateRenewalTargetModelLoading: false,
  })),
  on(updateRenewalTargetFailure, (state, action) => ({
    ...state,
    updateRenewalTargetModelLoading: false,
  })),

  /**
   * Custom fields
   */
  on(createCustomField, state => ({ ...state, createCustomFieldLoading: true })),
  on(createCustomFieldSuccess, (state, { field }) => ({
    ...state,
    customFields: [...state.customFields, field],
    createCustomFieldLoading: false,
  })),
  on(createCustomFieldFailure, state => ({ ...state, createCustomFieldLoading: false })),

  on(deleteCustomField, state => ({ ...state, deleteCustomFieldLoading: true })),
  on(deleteCustomFieldSuccess, state => ({ ...state, deleteCustomFieldLoading: false })),
  on(deleteCustomFieldFailure, state => ({ ...state, deleteCustomFieldLoading: false })),

  on(loadCustomFields, state => ({ ...state, customFieldsLoading: true })),
  on(loadCustomFieldsSuccess, (state, { customFields }) => ({
    ...state,
    customFields,
    customFieldsLoading: false,
  })),
  on(loadCustomFieldsFailure, state => ({
    ...state,
    customFieldsLoading: false,
  })),

  on(updateCustomField, state => ({ ...state, updateCustomFieldLoading: true })),
  on(updateCustomFieldSuccess, state => ({
    ...state,
    updateCustomFieldLoading: false,
  })),
  on(updateCustomFieldFailure, state => ({
    ...state,
    updateCustomFieldLoading: false,
  })),
  on(loadCustomFieldByContextId, state => ({
    ...state,
    CustomFieldByContextIdLoading: true,
  })),
  on(loadCustomFieldByContextIdSuccess, (state, { CustomFieldByContextId, contextId }) => ({
    ...state,
    CustomFieldByContextId: {
      ...state.CustomFieldByContextId,
      [contextId]: CustomFieldByContextId,
    },
    CustomFieldByContextIdLoading: false,
  })),
  on(loadCustomFieldByContextIdFailure, state => ({
    ...state,
    CustomFieldByContextIdLoading: false,
    CustomFieldByContextId: {},
  })),
  on(createBaseCustomFieldByContextId, state => ({
    ...state,
    CustomFieldByContextIdLoading: true,
  })),
  on(createBaseCustomFieldByContextIdSuccess, (state, { created }) => {
    if (!created || created.propertyBaseId == null) return state;

    const contextId = created.propertyBaseId;
    const existing = state.CustomFieldByContextId?.[contextId] ?? [];
    if (!Array.isArray(existing)) return state;

    return {
      ...state,
      CustomFieldByContextIdLoading: false,
      CustomFieldByContextId: {
        ...state.CustomFieldByContextId,
        [contextId]: [...existing, created],
      },
    };
  }),
  on(createBaseCustomFieldByContextIdFailure, state => ({
    ...state,
    CustomFieldByContextIdLoading: false,
  })),
  on(updateBaseCustomFieldByContextId, state => ({
    ...state,
    CustomFieldByContextIdLoading: true,
  })),
  on(updateBaseCustomFieldByContextIdSuccess, (state, { updated }) => {
    if (!updated || updated.propertyBaseId == null) return state;

    const contextId = updated.propertyBaseId;
    const updatedList = (state.CustomFieldByContextId?.[contextId] ?? []).map(item =>
      item.customFieldId === updated.customFieldId ? updated : item
    );
    return {
      ...state,
      CustomFieldByContextIdLoading: false,
      CustomFieldByContextId: {
        ...state.CustomFieldByContextId,
        [contextId]: updatedList,
      },
    };
  }),
  on(updateBaseCustomFieldByContextIdFailure, state => ({
    ...state,
    CustomFieldByContextIdLoading: false,
  })),
  on(deleteBaseCustomFieldByContextId, state => ({
    ...state,
    CustomFieldByContextIdLoading: true,
  })),
  on(deleteBaseCustomFieldByContextIdSuccess, (state, { customFieldId }) => {
    const updatedCustomFieldByContextId = { ...state.CustomFieldByContextId };
    for (const contextId in updatedCustomFieldByContextId) {
      updatedCustomFieldByContextId[contextId] = updatedCustomFieldByContextId[contextId].filter(
        f => f.customFieldId !== customFieldId
      );
    }
    return {
      ...state,
      CustomFieldByContextIdLoading: false,
      CustomFieldByContextId: updatedCustomFieldByContextId,
    };
  }),
  on(deleteBaseCustomFieldByContextIdFailure, state => ({
    ...state,
    CustomFieldByContextIdLoading: false,
  })),
  on(changeCustomFieldAccess, state => ({
    ...state,
    changeCustomFieldAccessLoading: true,
  })),
  on(changeCustomFieldAccessSuccess, state => ({
    ...state,
    changeCustomFieldAccessLoading: false,
  })),
  on(changeCustomFieldAccessFailure, state => ({
    ...state,
    changeCustomFieldAccessLoading: false,
  })),

  /**
   * placeholders
   */
  on(loadTemplatePlaceholders, state => ({
    ...state,
    templatePlaceholdersLoading: true,
  })),
  on(loadTemplatePlaceholdersSuccess, (state, { placeholders }) => ({
    ...state,
    templatePlaceholders: placeholders,
    templatePlaceholdersLoaded: true,
    templatePlaceholdersLoading: false,
  })),
  on(loadTemplatePlaceholdersFailure, (state, { error }) => ({
    ...state,
    templatePlaceholdersLoading: false,
    templatePlaceholdersLoaded: false,
  }))
);
